import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check, X } from 'lucide-react';
import { RenderIf } from '@/components/RenderIf';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { CanvasFilterDropdownProps } from '../types';
import { FilterButton } from './styled';

// Generic single-select filter backed by a URL search param, matching the
// dropdown filter UX used on configs/skills. The first option is the neutral
// default: selecting it (or having no param) clears the filter.
function CanvasFilterDropdown({ paramKey, label, options }: CanvasFilterDropdownProps) {
    const [searchParams, setSearchParams] = useSearchParams();
    const defaultOption = options[0];
    const activeValue = searchParams.get(paramKey);

    const activeOption = useMemo(
        () => options.find(option => option.value === activeValue) ?? null,
        [options, activeValue]
    );

    const isDefaultActive = !activeOption || activeOption.value === defaultOption.value;
    const TriggerIcon = isDefaultActive ? defaultOption.icon : activeOption!.icon;

    const handleSelect = ({ value }: { value: string }) => {
        const nextParams = new URLSearchParams(searchParams);
        if (value === defaultOption.value) {
            nextParams.delete(paramKey);
        } else {
            nextParams.set(paramKey, value);
        }
        setSearchParams(nextParams);
    };

    const handleClear = () => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete(paramKey);
        setSearchParams(nextParams);
    };

    return (
        <DropdownMenu>
            <FilterButton>
                <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-2">
                        <TriggerIcon size={14} />
                        <RenderIf isTrue={isDefaultActive}>
                            <span>{label}</span>
                        </RenderIf>
                        <RenderIf isTrue={!isDefaultActive}>
                            <span className="text-muted-foreground">{label}:</span>
                            <span>{activeOption?.label}</span>
                            <span
                                role="button"
                                aria-label="Clear filter"
                                className="flex items-center cursor-pointer"
                                onPointerDown={event => event.stopPropagation()}
                                onClick={event => {
                                    event.stopPropagation();
                                    handleClear();
                                }}
                            >
                                <X size={14} />
                            </span>
                        </RenderIf>
                    </Button>
                </DropdownMenuTrigger>
            </FilterButton>
            <DropdownMenuContent align="end" className="w-56">
                {options.map(option => {
                    const OptionIcon = option.icon;
                    const isSelected = isDefaultActive
                        ? option.value === defaultOption.value
                        : option.value === activeOption?.value;
                    return (
                        <DropdownMenuItem
                            key={option.value}
                            onClick={() => handleSelect({ value: option.value })}
                            className="gap-2"
                        >
                            <OptionIcon size={14} className="text-muted-foreground" />
                            <span className="flex-1">{option.label}</span>
                            <RenderIf isTrue={isSelected}>
                                <Check size={14} />
                            </RenderIf>
                        </DropdownMenuItem>
                    );
                })}
                <RenderIf isTrue={!isDefaultActive}>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleClear} className="gap-2">
                        <X size={14} />
                        <span>Clear filter</span>
                    </DropdownMenuItem>
                </RenderIf>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

export default CanvasFilterDropdown;
